import {
  breakpointSizes,
  getBreakpointUp,
  getFontCss,
  HoverableLink,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { createSaveSelectedVirtualCardIdsAction } from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.actions'
import {
  selectFilteredVirtualCards,
  selectFilteredVirtualCardsPermissions,
  selectSelectedVirtualCardListModels,
  selectUsersFullPaginatedDataFromState,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import FloatingToolbar from 'src/components/FloatingToolbar/FloatingToolbar'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import MoreDropdown, {
  MoreDropdownDesigns,
} from 'src/components/MoreDropdown/MoreDropdown'
import SvgAlertCircledFilled2 from 'src/components/Svgs/SvgAlertCircledFilled2'
import SvgClose from 'src/components/Svgs/SvgClose'
import SvgCloseCard from 'src/components/Svgs/SvgCloseCard'
import SvgSlashCircle from 'src/components/Svgs/SvgSlashCircle'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import TestId from 'src/components/TestId/TestId'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { VirtualCardTypes } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch, useQueryParamArrayHandler } from 'src/hooks'
import { BulkAction, useBulkAction } from 'src/hooks/bulkActionHooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import { connectedPush } from 'src/navigation/Lnk'
import {
  closeVirtualCardUrl,
  deactivateVirtualCardUrl,
  updateVirtualCardUrl,
} from 'src/navigation/urls'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import {
  createSetIsBulkCloseVirtualCardsPopupOpen,
  createSetIsBulkDeactivateVirtualCardsPopupOpen,
  createSetIsBulkUpdateVirtualCardsFlyoutOpen,
} from 'src/redux/actions/app.actions'
import {
  getVirtualCardPermissionsInBulk,
  getVirtualCards,
} from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

export const CARD_SELECTION_LIMIT = 200

const VirtualCardsAllCards: React.FC = () => {
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveDirectEnabledCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const {
    results: statisticsQueries,
    anyLoading: reactQueryStatisticsLoading,
  } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.VirtualCardsActiveCount,
      StatisticsKeys.VirtualCardsCount,
    ],
  })
  const virtualCardsCount = statisticsQueries?.VirtualCardsCount?.data ?? 0
  const appName = useSelector(selectAppName)
  const queryParams = useSelector(selectQueryParams)
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )
  const locale = useSelector(selectNullSafeLocaleFromState)
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const selectedVirtualCards = useSelector(selectSelectedVirtualCardListModels)
  const filteredVirtualCardsPermissions = useSelector(
    selectFilteredVirtualCardsPermissions,
  )
  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  // TODO: rename these to something more accurate (e.g. bulkUpdateLogic?)
  const [bulkUpdateFlyoutOpen, setBulkUpdateFlyoutOpen] =
    useState<boolean>(false)
  const [bulkDeactivatePopupOpen, setBulkDeactivatePopupOpen] =
    useState<boolean>(false)
  const [bulkClosePopupOpen, setBulkClosePopupOpen] = useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 150)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 150)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  const selectedVirtualCardsText = (
    selectedVirtualCards: VirtualCardListModel[],
  ): React.ReactElement => {
    let standardCount = 0
    let recurringCount = 0
    let standardGuestCount = 0
    let recurringGuestCount = 0
    const cardTypeArray = []

    selectedVirtualCards.map((vc) => {
      if (vc.recurs) {
        if (vc.direct) {
          recurringGuestCount++
        } else {
          recurringCount++
        }
      } else {
        if (vc.direct) {
          standardGuestCount++
        } else {
          standardCount++
        }
      }
    })

    if (standardCount > 0) {
      cardTypeArray.push(
        translateSingleString(locale, '${num} standard', {
          num: standardCount.toString(),
        }),
      )
    }
    if (recurringCount > 0) {
      cardTypeArray.push(
        translateSingleString(locale, '${num} recurring', {
          num: recurringCount.toString(),
        }),
      )
    }
    if (standardGuestCount > 0) {
      cardTypeArray.push(
        translateSingleString(locale, '${num} guest', {
          num: standardGuestCount.toString(),
        }),
      )
    }
    if (recurringGuestCount > 0) {
      cardTypeArray.push(
        translateSingleString(locale, '${num} recurring guest', {
          num: recurringGuestCount.toString(),
        }),
      )
    }

    return (
      <TestId testId={'selectedCardAmount'}>
        <Trns
          source={'${cardTypes} selected'}
          expressions={{
            cardTypes: cardTypeArray.join(', ').toString(),
          }}
        />
      </TestId>
    )
  }

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
    ])
  }, [dispatchStatistics])

  const fetchVirtualCards = (
    filtersBody?: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        count: 10,
        page: page,
        ...filtersBody,
      }),
    )
  }

  const infiniteScrollProps = {
    ...virtualCardsFullPaginatedData,
    fetch: fetchVirtualCards,
  }

  const readOnly =
    creditCardsActiveCanIssueVirtualCardsCount === 0 ||
    creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount === 0 ||
    creditCardsActiveDirectEnabledCanIssueVirtualCardsCount === 0

  const bulkVcnSelectedAnalyticsEvent = (totalSelectedCards: number): void => {
    analytics?.track('virtual cards - all cards - bulk VCN selected', {
      number_of_cards: totalSelectedCards,
    })
  }

  const { handleCheckboxClick, handleBulkAction } = useBulkAction(
    selectedVirtualCardIdsFromRedux || [],
    {
      updateSelectedIds: (selectedIds: string[]): void => {
        dispatch(createSaveSelectedVirtualCardIdsAction(selectedIds))
      },
      analyticsEvent: (): void => {
        bulkVcnSelectedAnalyticsEvent(
          selectedVirtualCardIdsFromRedux?.length || 0,
        )
      },
      limitCondition:
        selectedVirtualCardIdsFromRedux !== undefined &&
        selectedVirtualCardIdsFromRedux.length >= CARD_SELECTION_LIMIT,
      limitFunction: (): void => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <Trns
                source={
                  'You can only bulk update ${num} virtual cards at a time'
                }
                expressions={{
                  num: CARD_SELECTION_LIMIT.toString(),
                }}
              />
            ),
            partialSuccessColorScheme: true,
            image: (
              <SvgAlertCircledFilled2
                css={css`
                  transform: scale(0.8);
                  margin-left: -10px;
                `}
              />
            ),
          }),
        )
      },
    },
    {
      bulkAction: (selectedIds: string[]): void => {
        dispatch(getVirtualCardPermissionsInBulk(selectedIds))
      },
      analyticsEvent: (bulkAction): void => {
        const cardTypeArray: VirtualCardTypes[] = []

        selectedVirtualCards.map((vc) => {
          if (vc.recurs) {
            if (vc.direct) {
              cardTypeArray.push(VirtualCardTypes.recurringGuest)
            } else {
              cardTypeArray.push(VirtualCardTypes.recurring)
            }
          } else {
            if (vc.direct) {
              cardTypeArray.push(VirtualCardTypes.guestAccess)
            } else {
              cardTypeArray.push(VirtualCardTypes.standard)
            }
          }
        })

        analytics?.track(`bulk update vcn - ${bulkAction}`, {
          card_type: cardTypeArray.filter(
            (value, index, array) => array.indexOf(value) === index,
          ),
          number_of_cards: selectedVirtualCards.length,
        })
      },
    },
  )

  const canSelectVirtualCard = (virtualCard: VirtualCardListModel): boolean => {
    return !virtualCard.isBillPay
  }

  const handleBulkVirtualCardCheckboxClick = (): void => {
    if (filteredVirtualCards !== undefined) {
      if (selectedVirtualCards.length > 0) {
        bulkVcnSelectedAnalyticsEvent(0)
        dispatch(createSaveSelectedVirtualCardIdsAction([]))
      } else {
        const selectableCardIds = filteredVirtualCards
          .filter((vc) => canSelectVirtualCard(vc))
          .slice(0, CARD_SELECTION_LIMIT)
          .map((item) => item.id)
        bulkVcnSelectedAnalyticsEvent(selectableCardIds.length)
        dispatch(createSaveSelectedVirtualCardIdsAction(selectableCardIds))
      }
    }
  }

  // bulk update/deactivate virtual cards flyout/popup open
  useEffect(() => {
    // check if permissions are fully loaded in
    if (
      filteredVirtualCardsPermissions.filter(
        (permission) => permission?.permissions === undefined,
      ).length === 0
    ) {
      const cardsThatCanBeUpdated = filteredVirtualCardsPermissions.filter(
        (permission) =>
          permission?.permissions?.includes(
            VirtualCardPermissions.UpdateVirtualCard,
          ),
      )
      const cardsThatCantBeUpdated = filteredVirtualCardsPermissions.filter(
        (permission) =>
          !permission?.permissions?.includes(
            VirtualCardPermissions.UpdateVirtualCard,
          ),
      )

      if (
        (selectedVirtualCardIdsFromRedux?.length || 0) > 0 &&
        cardsThatCantBeUpdated.length === filteredVirtualCardsPermissions.length
      ) {
        // display can't update toast
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <Trns
                source={
                  'Oops! You can only edit virtual cards you have created or have access to as a budget user'
                }
              />
            ),
            failureColorScheme: true,
            hideImage: true,
          }),
        )
      } else {
        if (bulkUpdateFlyoutOpen) {
          if (cardsThatCanBeUpdated.length === 1) {
            dispatch(
              connectedPush(
                updateVirtualCardUrl(cardsThatCanBeUpdated[0].virtualCardId),
              ),
            )
          } else {
            dispatch(createSetIsBulkUpdateVirtualCardsFlyoutOpen(true))
          }
          setBulkUpdateFlyoutOpen(false)
        }
        if (bulkDeactivatePopupOpen) {
          if (
            cardsThatCanBeUpdated.length === 1 &&
            selectedVirtualCardIdsFromRedux?.length === 1
          ) {
            dispatch(
              connectedPush(
                deactivateVirtualCardUrl(
                  cardsThatCanBeUpdated[0].virtualCardId,
                ),
              ),
            )
          } else {
            dispatch(createSetIsBulkDeactivateVirtualCardsPopupOpen(true))
          }
          setBulkDeactivatePopupOpen(false)
        }
        if (bulkClosePopupOpen) {
          if (
            cardsThatCanBeUpdated.length === 1 &&
            selectedVirtualCardIdsFromRedux?.length === 1
          ) {
            dispatch(
              connectedPush(
                closeVirtualCardUrl(cardsThatCanBeUpdated[0].virtualCardId),
              ),
            )
          } else {
            dispatch(createSetIsBulkCloseVirtualCardsPopupOpen(true))
          }
          setBulkClosePopupOpen(false)
        }
      }
    }
  }, [
    filteredVirtualCardsPermissions,
    bulkUpdateFlyoutOpen,
    bulkDeactivatePopupOpen,
  ])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('all cards filter applied', {
        card_user:
          selectedFilters.card_user && selectedFilters.card_user.length > 0,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        deactivating_range: !!selectedFilters.deactivating_in,
      })
    }
  }, [selectedFilters])

  return !(
    statisticsLoaded.creditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveCanIssueVirtualCardsCountStatisticsLoaded
  ) || reactQueryStatisticsLoading ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`All Virtual Cards | ${appName}`}</title>
        <meta
          name="description"
          content={`All Virtual Cards page of ${appName}`}
        />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent
          noStackingIndex
          noPadding
        >
          <VirtualCardsBoxAndLinks
            virtualCards={filteredVirtualCards}
            emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
            infiniteScrollProps={infiniteScrollProps}
            initialFilterValues={{
              sortBy: 'activeClosedUpdatedAt',
              sortDescending: false,
              recipients: useQueryParamArrayHandler(queryParams.recipients),
              keyword: queryParams.search || '',
              statuses: useQueryParamArrayHandler(queryParams.statuses),
              cardTypes: useQueryParamArrayHandler(queryParams.cardTypes),
              deactivatingIn: queryParams.deactivatingIn,
            }}
            totalCount={virtualCardsCount}
            readOnly={readOnly}
            usersFullPaginatedData={usersFullPaginatedData}
            checkboxProps={{
              selectedVirtualCards: selectedVirtualCards,
              handleVirtualCardCheckboxClick: () => undefined, // DEPRECATED
              handleBulkVirtualCardCheckboxClick:
                handleBulkVirtualCardCheckboxClick,
              handleDataGridVirtualCardCheckboxClick: handleCheckboxClick,
              total: virtualCardsFullPaginatedData.totalItems || 0,
              canSelectVirtualCard: canSelectVirtualCard,
            }}
            setSelectedFilters={setSelectedFilters}
            clientHeight={clientHeight || 0}
          />
          <div>
            <FloatingToolbar
              noPadding
              visible={selectedVirtualCards.length > 0}
            >
              <div
                css={css`
                  display: flex;
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  align-items: center;
                  ${getBreakpointUp(breakpointSizes.MD)(css`
                    height: 60px;
                  `)}
                `}
              >
                <span
                  css={css`
                    padding: 20px 40px;
                  `}
                >
                  {selectedVirtualCardsText(selectedVirtualCards)}
                </span>
                <>
                  <TestId
                    testId={'bulkUpdateButton'}
                    css={css`
                      height: 100%;
                      align-items: center;
                      border-left: 1px solid
                        ${styleConstants.colors.gray_medium};
                      border-right: 1px solid
                        ${styleConstants.colors.gray_medium};
                    `}
                  >
                    <HoverableLink
                      css={css`
                        height: 100%;
                        padding: 15px 5px;
                      `}
                      onClick={(): void => {
                        handleBulkAction(BulkAction.Update)
                        setBulkUpdateFlyoutOpen(true)
                      }}
                      ariaLabel="Update"
                      regular
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 4px;
                          padding: 10px;
                          border-radius: 4px;

                          &:hover {
                            background-color: ${styleConstants.colors
                              .blue_opacity};
                          }
                        `}
                      >
                        <SvgUpdate
                          css={css`
                            transform: scale(0.75);
                          `}
                        />
                        <Trns source={'Update'} />
                      </div>
                    </HoverableLink>
                  </TestId>
                  <MoreDropdown
                    css={css`
                      display: flex;
                      position: relative;
                      justify-content: center;
                      align-items: center;
                      width: 36px;
                      height: 36px;
                      margin: 0px 8px;
                    `}
                    design={MoreDropdownDesigns.CtaToWhite}
                    alignRight
                    width={'300px'}
                    bottomOptions={[
                      {
                        key: 'deactivate',
                        label: (
                          <div
                            css={css`
                              ${getFontCss(
                                14,
                                styleConstants.colors.red,
                                styleConstants.fontFamilies.regular,
                              )};
                              display: flex;
                              align-items: center;
                              gap: 8px;
                            `}
                          >
                            <SvgSlashCircle />
                            <Trns source={'Deactivate'} />
                          </div>
                        ),
                        onClick: (): void => {
                          handleBulkAction(BulkAction.Deactivate)
                          setBulkDeactivatePopupOpen(true)
                        },
                        ariaLabelSource: 'Deactivate',
                      },
                      {
                        key: 'closeVirtualCards',
                        label: (
                          <div
                            css={css`
                              ${getFontCss(
                                14,
                                styleConstants.colors.red,
                                styleConstants.fontFamilies.regular,
                              )};
                              display: flex;
                              align-items: flex-start;
                              gap: 8px;
                            `}
                          >
                            <SvgCloseCard
                              css={css`
                                flex-grow: 1;
                              `}
                              fill={styleConstants.colors.red}
                            />
                            <div>
                              <Trns source={'Close Virtual Cards'} />
                              <div
                                css={css`
                                  ${getFontCss(
                                    12,
                                    styleConstants.colors.gray_darkest,
                                    styleConstants.fontFamilies.regular,
                                  )};
                                `}
                              >
                                <Trns
                                  source={
                                    'Permanently deactivate these virtual cards'
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ),
                        onClick: (): void => {
                          handleBulkAction(BulkAction.CloseCards)
                          setBulkClosePopupOpen(true)
                        },
                        ariaLabelSource: 'Close Virtual Cards',
                      },
                    ]}
                    topOptions={[]}
                    hover
                    fillIcon
                    openAbove
                  />
                </>
                <HoverableLink
                  ariaLabelSource={'Close'}
                  onClick={(): void => {
                    dispatch(createSaveSelectedVirtualCardIdsAction([]))
                  }}
                >
                  <div
                    css={css`
                      display: flex;
                      position: relative;
                      width: 36px;
                      height: 36px;
                      align-items: center;
                      justify-content: center;
                      margin: 0px 8px;

                      svg {
                        transition: stroke 0.2s ease;
                      }

                      svg path {
                        stroke: ${themeConstants.colors.font_primary};
                        transition: stroke-width 0.2s ease;
                      }

                      &:hover {
                        svg path {
                          stroke: ${themeConstants.colors.cta};
                        }

                        &::before {
                          content: '';
                          position: absolute;
                          top: 0;
                          left: 0;
                          width: 100%;
                          height: 100%;
                          background-color: rgba(0, 0, 0, 0.05);
                          border-radius: 8px;
                          transition: background-color 0.2s ease;
                        }
                      }
                    `}
                  >
                    <SvgClose />
                  </div>
                </HoverableLink>
              </div>
            </FloatingToolbar>
          </div>
        </BackgroundPageContent>
      </div>
    </>
  )
}

export default VirtualCardsAllCards
